import React from "react";
import { intervalToDuration } from "date-fns/fp";
import { useTheme } from "styled-components";
import {
  faBirthdayCake,
  faCalendarCheck,
  faFileAlt,
  faFileImport,
  faMessage,
  IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import Message from "./Message";
import { NotificationContainer } from "../../accounts/company/pages/overview/styles";
import { NotificationType } from "../../redux/api-model/topbar/notifications";
import { TextVariant, TypographyNew } from "../typography/typography";
import NotificationCard from "./NotificationCard";
import { NotificationCardData } from "./notification";
import { useTranslation } from "react-i18next";

export interface StatusNotificationProps {
  date?: string;
  title: string;
  body: string;
  type: NotificationType;
  data: NotificationCardData;
}

type StatusTypeValues = Record<NotificationType, IconDefinition>;

const statusTypeValues: StatusTypeValues = {
  [NotificationType.MANAGER_EMPLOYEE_BIRTHDAY]: faBirthdayCake,
  [NotificationType.CHANGE_REJECTED]: faFileAlt,
  [NotificationType.CHANGE_APPROVED]: faFileAlt,
  [NotificationType.CHANGE_REQUESTED]: faFileAlt,
  [NotificationType.EMPLOYEE_BIRTHDAY]: faBirthdayCake,
  [NotificationType.EMPLOYEE_CONTRACT_STARTING]: faFileAlt,
  [NotificationType.EMPLOYEE_VISA_EXPIRING]: faFileAlt,
  [NotificationType.EMPLOYEE_CONTRACT_ENDING]: faFileImport,
  [NotificationType.ABSENCE_REQUEST_REQUESTED]: faCalendarCheck,
};

const getNotificationTime = (
  date: string
): { days?: number; message: string } => {
  const today = new Date();
  const dateToCheck = new Date(date);
  if (today.getTime() >= dateToCheck.getTime()) {
    const { days } = intervalToDuration({
      start: new Date(date),
      end: new Date(),
    });

    if (days === 0) {
      return {
        days,
        message: "time.today",
      };
    }

    if (days === 1) {
      return {
        days,
        message: "time.yesterday",
      };
    }

    return {
      days,
      message: "time.daysAgo",
    };
  }

  const { days } = intervalToDuration({
    start: new Date(),
    end: new Date(date),
  });

  if (days === 1) {
    return {
      days,
      message: "time.tomorrow",
    };
  }

  return {
    days,
    message: "time.inDays",
  };
};

const StatusNotification: React.FC<StatusNotificationProps> = ({
  date,
  title,
  body,
  type,
  data,
}) => {
  const { t } = useTranslation("notifications");
  const icon = statusTypeValues[type] ?? faMessage;
  const theme = useTheme();
  const notificationTime = date ? getNotificationTime(date) : null;

  return (
    <NotificationContainer>
      <Message
        icon={icon}
        component1={<NotificationCard title={title} body={body} data={data} />}
        component2={
          date ? (
            <TypographyNew
              align="right"
              variant={TextVariant.body1}
              color={theme.colors.grays3}
            >
              {notificationTime
                ? t(notificationTime.message, { days: notificationTime.days })
                : null}
            </TypographyNew>
          ) : (
            <></>
          )
        }
      />
    </NotificationContainer>
  );
};

export default StatusNotification;
